import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { Bar, Button, Title, Input, Icon, FlexBox, SegmentedButton, SegmentedButtonItem, MessageStrip, BusyIndicator, Text, Select, Option, Card, CheckBox, Label, Panel, Tag } from '@ui5/webcomponents-react';
import type { ScreenDef, FieldSection, CollectionSection, MetaEntitySummary } from '@b1links/core';
import { api, type EntityMeta } from '../../../api/client';
import { useAuth } from '../../../store/auth';
import { PropertiesPanel, type Selection } from './PropertiesPanel';
import { PaletteItem, SortableChip, DropZone } from './dnd';
import { ActionsEditor } from './ActionsEditor';
import { humanize, labelFor } from '../../../render/format';
import { useIsMobile } from '../../../hooks/useIsMobile';

type Pane = 'palette' | 'canvas' | 'props';

const blankScreen = (entity: string): ScreenDef => ({
  id: '', title: entity, entity, icon: 'document',
  list: { columns: [], pageSize: 50 },
  form: { canCreate: true, canEdit: true, sections: [{ kind: 'fields', title: 'General', columns: 2, fields: [] }] },
  menu: { group: 'Screens', order: 100, showOnHome: true },
});

type Tab = 'form' | 'list' | 'actions';

/** Drag-and-drop screen designer. Palette (metadata fields) → canvas (form sections / list columns) → publish to SAP. */
export function DesignerPage() {
  const { screenId } = useParams();
  const nav = useNavigate();
  const can = useAuth(s => s.can);
  const qc = useQueryClient();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const existing = useQuery({ queryKey: ['screen', screenId], queryFn: () => api.screen(screenId!), enabled: !!screenId });
  const entityList = useQuery({ queryKey: ['entities-summary'], queryFn: () => api.metaEntities(), enabled: !screenId, staleTime: Infinity });

  const [def, setDef] = useState<ScreenDef | null>(null);
  const [tab, setTab] = useState<Tab>('form');
  const [sel, setSel] = useState<Selection>(null);
  const [palSearch, setPalSearch] = useState('');
  const [msg, setMsg] = useState<{ design: 'Positive' | 'Negative'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  // Phone: the three panes (palette · canvas · properties) cannot sit side by side, so show one at a time.
  const isMobile = useIsMobile();
  const [pane, setPane] = useState<Pane>('canvas');
  const select = (s: Selection) => { setSel(s); if (isMobile && s) setPane('props'); };

  // initialise def from existing screen once loaded
  const ready = screenId ? existing.isSuccess : true;
  if (screenId && existing.data && !def) setDef(structuredClone(existing.data));

  const entity = def?.entity;
  const meta = useQuery({ queryKey: ['meta', entity], queryFn: () => api.entityMeta(entity!), enabled: !!entity, staleTime: Infinity });

  const used = useMemo(() => {
    const s = new Set<string>();
    def?.form.sections.forEach(sec => sec.kind === 'fields' && sec.fields.forEach(f => s.add(f.field)));
    return s;
  }, [def]);

  if (!ready) return <FlexBox justifyContent="Center" style={{ padding: '3rem' }}><BusyIndicator active /></FlexBox>;

  // ----- new screen: pick an entity -----
  if (!def) {
    const list = (entityList.data ?? []).filter(e => !palSearch || e.set.toLowerCase().includes(palSearch.toLowerCase()) || e.type.toLowerCase().includes(palSearch.toLowerCase()));
    return (
      <div>
        <Title level="H3" style={{ marginBottom: '0.5rem' }}>New screen — choose an entity</Title>
        <Input placeholder="Search 300+ Service Layer entities…" value={palSearch} icon={<Icon name="search" />} style={{ width: 'min(28rem,100%)', marginBottom: '1rem' }} onInput={e => setPalSearch(e.target.value ?? '')} />
        {entityList.isLoading && <BusyIndicator active />}
        <FlexBox wrap="Wrap" style={{ gap: '0.5rem' }}>
          {list.slice(0, 120).map(e => (
            <Card key={e.set} style={{ width: '15rem', cursor: 'pointer' }} onClick={() => { const d = blankScreen(e.set); d.title = e.type; setDef(d); setSel({ kind: 'screen' }); }}>
              <div style={{ padding: '0.6rem 0.8rem' }}>
                <FlexBox alignItems="Center" style={{ gap: '0.4rem' }}><Text style={{ fontWeight: 600 }}>{e.set}</Text>{e.isUdt && <Tag colorScheme="8">UDT</Tag>}{e.isUdo && <Tag colorScheme="7">UDO</Tag>}</FlexBox>
                <Text style={{ color: 'var(--sapNeutralTextColor)', fontSize: '0.75rem' }}>{e.category} · {e.fieldCount} fields{e.hasAppUser ? ' · U_AppUser' : ''}</Text>
              </div>
            </Card>
          ))}
        </FlexBox>
        <Button style={{ marginTop: '1rem' }} onClick={() => nav('/admin/screens')}>Cancel</Button>
      </div>
    );
  }

  const update = (next: ScreenDef) => setDef(next);
  const palette = (meta.data?.fields ?? []).filter(f =>
    f.kind !== 'collection' && f.kind !== 'complex' &&
    (!palSearch || f.name.toLowerCase().includes(palSearch.toLowerCase()) || (f.description ?? '').toLowerCase().includes(palSearch.toLowerCase())));
  const collections = (meta.data?.fields ?? []).filter(f => f.kind === 'collection');

  const addFieldToSection = (secIdx: number, field: string) => {
    if (used.has(field)) return;
    const d = structuredClone(def); const sec = d.form.sections[secIdx];
    if (sec?.kind === 'fields') { sec.fields.push({ field }); setDef(d); setSel({ kind: 'field', section: secIdx, index: sec.fields.length - 1 }); }
  };
  const addColumn = (field: string) => {
    if (def.list.columns.some(c => c.field === field)) return;
    const d = structuredClone(def); const mf = meta.data?.fields.find(f => f.name === field);
    d.list.columns.push({ field, format: mf?.kind === 'decimal' ? 'number' : mf?.kind === 'datetime' ? 'date' : undefined });
    setDef(d); setSel({ kind: 'column', index: d.list.columns.length - 1 });
  };

  const onDragEnd = (e: DragEndEvent) => {
    const a = String(e.active.id), o = e.over ? String(e.over.id) : '';
    if (!o) return;
    if (a.startsWith('pal:')) {                       // palette field dropped
      const field = a.slice(4);
      if (tab === 'form') { const m = o.match(/^(?:sec|fld):(\d+)/); if (m) addFieldToSection(Number(m[1]), field); }
      else if (o === 'list' || o.startsWith('col:')) addColumn(field);
      return;
    }
    if (a.startsWith('fld:') && o.startsWith('fld:')) {   // reorder within a section
      const [, as, ai] = a.split(':'), [, os, oi] = o.split(':');
      if (as !== os) return;
      const d = structuredClone(def); const sec = d.form.sections[Number(as)] as FieldSection;
      sec.fields = arrayMove(sec.fields, Number(ai), Number(oi)); setDef(d); return;
    }
    if (a.startsWith('col:') && o.startsWith('col:')) {   // reorder columns
      const d = structuredClone(def); d.list.columns = arrayMove(d.list.columns, Number(a.slice(4)), Number(o.slice(4))); setDef(d);
    }
  };

  const addSection = (kind: 'fields' | 'collection') => {
    const d = structuredClone(def);
    d.form.sections.push(kind === 'fields' ? { kind: 'fields', title: 'Section', columns: 2, fields: [] } : { kind: 'collection', title: 'Lines', collection: collections[0]?.name ?? '', columns: [] });
    setDef(d); setSel({ kind: 'section', index: d.form.sections.length - 1 });
  };

  const publish = async () => {
    if (!def.id) { setMsg({ design: 'Negative', text: 'Set a screen id in Screen properties' }); setSel({ kind: 'screen' }); return; }
    setBusy(true); setMsg(null);
    try { const saved = await api.saveScreen(def); setDef(saved);
      qc.invalidateQueries({ queryKey: ['screen', saved.id] }); qc.invalidateQueries({ queryKey: ['screens'] }); qc.invalidateQueries({ queryKey: ['screens-all'] }); qc.invalidateQueries({ queryKey: ['list', saved.id] });
      setMsg({ design: 'Positive', text: `Published v${saved.version}. It is now live in the menu.` }); }
    catch (e) { setMsg({ design: 'Negative', text: (e as Error).message }); } finally { setBusy(false); }
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <FlexBox direction="Column" style={{ height: isMobile ? 'calc(100dvh - 4rem)' : 'calc(100vh - 3rem)' }}>
        {(() => {
          const tabs = (
            <SegmentedButton onSelectionChange={e => setTab((e.detail.selectedItems[0] as HTMLElement).dataset.t as Tab)}>
              <SegmentedButtonItem data-t="form" selected={tab === 'form'} icon="form">Form</SegmentedButtonItem>
              <SegmentedButtonItem data-t="list" selected={tab === 'list'} icon="table-view">List</SegmentedButtonItem>
              <SegmentedButtonItem data-t="actions" selected={tab === 'actions'} icon="action">Actions</SegmentedButtonItem>
            </SegmentedButton>);
          const screenBtn = <Button icon="settings" onClick={() => select({ kind: 'screen' })}>Screen</Button>;
          const previewBtn = def.id ? <Button icon="show" onClick={() => window.open(`#/s/${def.id}`, '_blank')}>Preview</Button> : null;
          const publishBtn = <Button design="Emphasized" icon="save" disabled={busy} onClick={publish}>{busy ? 'Publishing…' : 'Publish'}</Button>;
          if (!isMobile) return (
            <Bar design="Header" startContent={<><Title level="H4">Designer</Title><Text style={{ color: 'var(--sapNeutralTextColor)' }}>{def.entity}{def.id ? ` · ${def.id}` : ' · (unsaved)'}</Text></>}
              endContent={<>{tabs}{screenBtn}{previewBtn}{publishBtn}</>} />);
          // Phone header: title + Publish on one line, then the tab switcher and pane switcher wrap underneath.
          return (
            <div style={{ borderBottom: '1px solid var(--sapGroup_ContentBorderColor,#eee)', paddingBottom: '0.4rem' }}>
              <FlexBox justifyContent="SpaceBetween" alignItems="Center" style={{ gap: '0.5rem', padding: '0.25rem 0' }}>
                <div style={{ minWidth: 0 }}><Title level="H5" style={{ margin: 0 }}>Designer</Title><Text style={{ color: 'var(--sapNeutralTextColor)', fontSize: '0.75rem' }}>{def.entity}{def.id ? ` · ${def.id}` : ' · (unsaved)'}</Text></div>
                {publishBtn}
              </FlexBox>
              <FlexBox wrap="Wrap" alignItems="Center" style={{ gap: '0.4rem' }}>
                {tabs}{screenBtn}{previewBtn}
                <SegmentedButton onSelectionChange={e => setPane((e.detail.selectedItems[0] as HTMLElement).dataset.p as Pane)}>
                  <SegmentedButtonItem data-p="palette" selected={pane === 'palette'} icon="list">Fields</SegmentedButtonItem>
                  <SegmentedButtonItem data-p="canvas" selected={pane === 'canvas'} icon="form">Canvas</SegmentedButtonItem>
                  <SegmentedButtonItem data-p="props" selected={pane === 'props'} icon="settings">Properties</SegmentedButtonItem>
                </SegmentedButton>
              </FlexBox>
            </div>);
        })()}
        {msg && <MessageStrip design={msg.design} onClose={() => setMsg(null)}>{msg.text}</MessageStrip>}

        <FlexBox style={{ flex: 1, minHeight: 0 }}>
          {/* palette */}
          <div style={isMobile
            ? { display: pane === 'palette' ? 'block' : 'none', width: '100%', overflow: 'auto', padding: '0.5rem' }
            : { width: '16rem', borderRight: '1px solid var(--sapGroup_ContentBorderColor,#eee)', overflow: 'auto', padding: '0.5rem' }}>
            {isMobile && <Text style={{ display: 'block', fontSize: '0.75rem', color: 'var(--sapNeutralTextColor)', marginBottom: '0.4rem' }}>Tap a field to add it to the {tab === 'form' ? 'selected section' : 'list'}.</Text>}
            <Input placeholder="Filter fields…" value={palSearch} icon={<Icon name="search" />} style={{ width: '100%', marginBottom: '0.5rem' }} onInput={e => setPalSearch(e.target.value ?? '')} />
            {meta.isLoading && <BusyIndicator active />}
            <FlexBox direction="Column" style={{ gap: '0.3rem' }}>
              {palette.map(f => {
                const inUse = tab === 'form' ? used.has(f.name) : def.list.columns.some(c => c.field === f.name);
                return (
                  <PaletteItem key={f.name} id={`pal:${f.name}`}>
                    <FlexBox alignItems="Center" justifyContent="SpaceBetween"
                      style={{ opacity: inUse ? 0.4 : 1, border: '1px solid var(--sapField_BorderColor,#ccc)', borderRadius: '0.4rem', padding: '0.3rem 0.5rem', background: 'var(--sapField_Background,#fff)' }}
                      onClick={isMobile ? () => (tab === 'form' ? addFieldToSection(typeof (sel as { section?: number })?.section === 'number' ? (sel as { section: number }).section : 0, f.name) : addColumn(f.name)) : undefined}
                      onDoubleClick={() => tab === 'form' ? addFieldToSection(typeof (sel as { section?: number })?.section === 'number' ? (sel as { section: number }).section : 0, f.name) : addColumn(f.name)}>
                      <div style={{ minWidth: 0 }}>
                        <Text style={{ fontSize: '0.8125rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>{f.description || humanize(f.name)}</Text>
                        <Text style={{ fontSize: '0.7rem', color: 'var(--sapNeutralTextColor)' }}>{f.name} · {f.kind}</Text>
                      </div>
                      {f.isUdf && <Tag colorScheme="8" style={{ marginLeft: 4 }}>UDF</Tag>}
                    </FlexBox>
                  </PaletteItem>
                );
              })}
            </FlexBox>
          </div>

          {/* canvas */}
          <div style={{ flex: 1, minWidth: 0, overflow: 'auto', padding: isMobile ? '0.5rem 0' : '0.75rem', background: 'var(--sapBackgroundColor,#f5f6f7)', display: isMobile && pane !== 'canvas' ? 'none' : undefined }}>
            {tab === 'actions' ? <ActionsEditor def={def} meta={meta.data} onChange={update} /> : tab === 'form' ? (
              <>
                {def.form.sections.map((sec, si) => (
                  <Card key={si} style={{ marginBottom: '0.75rem' }}
                    header={<FlexBox alignItems="Center" justifyContent="SpaceBetween" wrap="Wrap" style={{ padding: '0.5rem 0.75rem', cursor: 'pointer', gap: '0.25rem 0.5rem' }} onClick={() => select({ kind: 'section', index: si })}>
                      <Title level="H6">{sec.title || (sec.kind === 'collection' ? sec.collection : 'Section')} {sec.kind === 'collection' && <Tag colorScheme="7">table</Tag>}</Title>
                      <Text style={{ color: 'var(--sapNeutralTextColor)', fontSize: '0.75rem' }}>{sec.kind === 'fields' ? `${sec.fields.length} fields · ${sec.columns ?? 2} col` : sec.collection}</Text>
                    </FlexBox>}>
                    <div style={{ padding: '0.6rem 0.75rem' }}>
                      {sec.kind === 'fields' ? (
                        <DropZone id={`sec:${si}`} style={{ minHeight: '2.5rem' }}>
                          <SortableContext items={sec.fields.map((_, i) => `fld:${si}:${i}`)} strategy={horizontalListSortingStrategy}>
                            <FlexBox wrap="Wrap" style={{ gap: '0.4rem' }}>
                              {sec.fields.map((f, fi) => (
                                <SortableChip key={f.field} id={`fld:${si}:${fi}`} selected={sel?.kind === 'field' && sel.section === si && sel.index === fi} onClick={() => select({ kind: 'field', section: si, index: fi })}>
                                  {f.label ?? f.field}{f.required ? ' *' : ''}
                                </SortableChip>
                              ))}
                              {sec.fields.length === 0 && <Text style={{ color: 'var(--sapNeutralTextColor)', fontSize: '0.8rem' }}>Drag fields here (or double-click in palette)</Text>}
                            </FlexBox>
                          </SortableContext>
                        </DropZone>
                      ) : <CollectionEditor def={def} meta={meta.data} index={si} onChange={update} />}
                    </div>
                  </Card>
                ))}
                <FlexBox wrap="Wrap" style={{ gap: '0.5rem' }}>
                  <Button icon="add" onClick={() => addSection('fields')}>Add section</Button>
                  <Button icon="add" design="Transparent" onClick={() => addSection('collection')}>Add child table</Button>
                </FlexBox>
              </>
            ) : (
              <Card header={<Title level="H6" style={{ padding: '0.5rem 0.75rem' }}>List columns — drag to reorder, drop fields from palette</Title>}>
                <div style={{ padding: '0.6rem 0.75rem' }}>
                  <DropZone id="list" style={{ minHeight: '2.5rem' }}>
                    <SortableContext items={def.list.columns.map((_, i) => `col:${i}`)} strategy={horizontalListSortingStrategy}>
                      <FlexBox wrap="Wrap" style={{ gap: '0.4rem' }}>
                        {def.list.columns.map((c, ci) => (
                          <SortableChip key={c.field} id={`col:${ci}`} selected={sel?.kind === 'column' && sel.index === ci} onClick={() => select({ kind: 'column', index: ci })}>
                            {c.label ?? labelFor(c.field, meta.data?.fields.find(f => f.name === c.field))}{c.format && c.format !== 'text' ? ` · ${c.format}` : ''}
                          </SortableChip>
                        ))}
                        {def.list.columns.length === 0 && <Text style={{ color: 'var(--sapNeutralTextColor)', fontSize: '0.8rem' }}>Drag fields here (or double-click in palette)</Text>}
                      </FlexBox>
                    </SortableContext>
                  </DropZone>
                  <ListOptions def={def} onChange={update} />
                </div>
              </Card>
            )}
          </div>

          {/* properties */}
          <div style={isMobile
            ? { display: pane === 'props' ? 'block' : 'none', width: '100%', overflow: 'auto', padding: '0.5rem 0.25rem' }
            : { width: '20rem', borderLeft: '1px solid var(--sapGroup_ContentBorderColor,#eee)', overflow: 'auto', padding: '0.75rem' }}>
            <PropertiesPanel def={def} meta={meta.data} sel={sel} onChange={update} />
          </div>
        </FlexBox>
      </FlexBox>
    </DndContext>
  );
}

function ListOptions({ def, onChange }: { def: ScreenDef; onChange(d: ScreenDef): void }) {
  const patch = (fn: (d: ScreenDef) => void) => { const d = structuredClone(def); fn(d); onChange(d); };
  return (
    <Panel headerText="List options" collapsed style={{ marginTop: '0.75rem' }}>
      <div style={{ padding: '0.5rem' }}>
        <Label>Search fields (csv)</Label>
        <Input value={(def.list.searchFields ?? []).join(',')} style={{ width: '100%', marginBottom: '0.5rem' }} onInput={e => patch(d => { d.list.searchFields = (e.target.value ?? '').split(',').map(s => s.trim()).filter(Boolean); })} />
        <Label>Order by (e.g. "DocEntry desc")</Label>
        <Input value={def.list.orderby ?? ''} style={{ width: '100%', marginBottom: '0.5rem' }} onInput={e => patch(d => { d.list.orderby = e.target.value || undefined; })} />
        <Label>Base filter (OData)</Label>
        <Input value={def.list.filter ?? ''} style={{ width: '100%', marginBottom: '0.5rem' }} onInput={e => patch(d => { d.list.filter = e.target.value || undefined; })} />
        <FlexBox wrap="Wrap" style={{ gap: '1rem' }}>
          <div><Label>Page size</Label><Input type="Number" value={String(def.list.pageSize ?? 50)} onInput={e => patch(d => { d.list.pageSize = Number(e.target.value) || 50; })} /></div>
          <div style={{ display: 'flex', alignItems: 'end' }}><CheckBox text={'"Mine only" toggle'} checked={!!def.list.mineFilter} onChange={e => patch(d => { d.list.mineFilter = e.target.checked; })} /></div>
        </FlexBox>
      </div>
    </Panel>
  );
}

function CollectionEditor({ def, meta, index, onChange }: { def: ScreenDef; meta?: EntityMeta; index: number; onChange(d: ScreenDef): void }) {
  const sec = def.form.sections[index] as CollectionSection;
  const ct = meta?.fields.find(f => f.name === sec.collection)?.complexType;
  const cols = ct ? meta?.complexTypes[ct]?.fields ?? [] : [];
  const patch = (fn: (s: CollectionSection) => void) => { const d = structuredClone(def); fn(d.form.sections[index] as CollectionSection); onChange(d); };
  const has = (f: string) => sec.columns.some(c => c.field === f);
  return (
    <div>
      <FlexBox wrap="Wrap" style={{ gap: '0.4rem', marginBottom: '0.5rem' }}>
        {sec.columns.map((c, ci) => (
          <Tag key={c.field} colorScheme="6" onClick={() => patch(s => { s.columns.splice(ci, 1); const i = (s.editable ?? []).indexOf(c.field); if (i >= 0) s.editable!.splice(i, 1); })} style={{ cursor: 'pointer' }}>
            {c.label ?? c.field} ✕
          </Tag>
        ))}
        {sec.columns.length === 0 && <Text style={{ color: 'var(--sapNeutralTextColor)', fontSize: '0.8rem' }}>Add columns from the child table below</Text>}
      </FlexBox>
      <Select style={{ width: 'min(14rem, 100%)' }} onChange={e => { const f = e.detail.selectedOption.dataset.v; if (f) patch(s => { s.columns.push({ field: f }); }); }}>
        <Option data-v="">+ add column…</Option>
        {cols.filter(c => !has(c.name)).slice(0, 60).map(c => <Option key={c.name} data-v={c.name}>{c.name}</Option>)}
      </Select>
      <FlexBox wrap="Wrap" style={{ gap: '0.75rem', marginTop: '0.5rem' }}>
        {sec.columns.map(c => (
          <CheckBox key={c.field} text={`edit ${c.field}`} checked={(sec.editable ?? []).includes(c.field)}
            onChange={e => patch(s => { s.editable = s.editable ?? []; const i = s.editable.indexOf(c.field); if (e.target.checked && i < 0) s.editable.push(c.field); else if (!e.target.checked && i >= 0) s.editable.splice(i, 1); })} />
        ))}
      </FlexBox>
    </div>
  );
}
